import { CalendarDays, Mail, MailX, Users } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface Signals {
  eventsAndEmail: number;
  emailOnly: number;
  eventsOnly: number;
  neither: number;
  recencyUnknown: number;
}

/**
 * How much of the membership each source can actually see.
 *
 * The point of the panel is the `emailOnly` segment: members who registered for
 * nothing all year but are opening what the chamber sends. In the AMS they are
 * indistinguishable from a dead account, which is the gap this product exists
 * to close — so it gets called out in words, not just coloured.
 */
export function SignalCoverage({ signals, className }: { signals: Signals; className?: string }) {
  const total =
    signals.eventsAndEmail + signals.emailOnly + signals.eventsOnly + signals.neither;
  if (total === 0) return null;

  const segments = [
    {
      key: 'eventsAndEmail',
      label: 'Events and email',
      value: signals.eventsAndEmail,
      bar: 'bg-bucket-engaged',
      dot: 'text-bucket-engaged',
      icon: Users,
      note: 'Visible everywhere',
    },
    {
      key: 'emailOnly',
      label: 'Email only',
      value: signals.emailOnly,
      bar: 'bg-primary',
      dot: 'text-primary',
      icon: Mail,
      note: 'Invisible to the AMS',
    },
    {
      key: 'eventsOnly',
      label: 'Events only',
      value: signals.eventsOnly,
      bar: 'bg-bucket-watching',
      dot: 'text-bucket-watching',
      icon: CalendarDays,
      note: 'Shows up, ignores email',
    },
    {
      key: 'neither',
      label: 'Neither',
      value: signals.neither,
      bar: 'bg-muted-foreground/40',
      dot: 'text-muted-foreground',
      icon: MailX,
      note: 'No signal at all',
    },
  ];

  const pct = (n: number) => (n / total) * 100;

  return (
    <div className={className}>
      <div className="flex h-3 overflow-hidden rounded-full bg-muted">
        {segments.map((s) =>
          s.value === 0 ? null : (
            <div
              key={s.key}
              className={cn('h-full first:rounded-l-full last:rounded-r-full', s.bar)}
              style={{ width: `${pct(s.value)}%` }}
              title={`${s.label}: ${s.value.toLocaleString()}`}
            />
          ),
        )}
      </div>

      <dl className="mt-5 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        {segments.map((s) => (
          <div key={s.key} className="flex items-start gap-3">
            <s.icon className={cn('mt-0.5 h-4 w-4 shrink-0', s.dot)} />
            <div className="min-w-0">
              <dt className="flex items-baseline gap-2 text-sm font-medium">
                {s.label}
                <span className="text-xs font-normal text-muted-foreground">
                  {Math.round(pct(s.value))}%
                </span>
              </dt>
              <dd className="text-sm tabular-nums">
                {s.value.toLocaleString()}
                <span className="ml-1.5 text-xs text-muted-foreground">{s.note}</span>
              </dd>
            </div>
          </div>
        ))}
      </dl>

      {signals.emailOnly > 0 && (
        <p className="mt-5 rounded-lg bg-primary/5 px-4 py-3 text-sm ring-1 ring-primary/15">
          <span className="font-semibold text-primary">
            {signals.emailOnly.toLocaleString()} members
          </span>{' '}
          attended nothing this year but are still opening the chamber's email. They look dormant in
          MC&nbsp;Trade and are the clearest place to spend an invitation.
        </p>
      )}
    </div>
  );
}
